import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  Box,
  Typography,
  Tooltip,
  alpha,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export interface ActionMenuSection {
  title?: string;
  items: ActionMenuItem[];
}

export interface ActionMenuItem {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  subItems?: ActionMenuItem[];
  danger?: boolean;
  divider?: boolean;
  /**
   * Shown on hover. Use with `disabled` to explain why an action is unavailable:
   * a disabled MenuItem has no pointer events, so a wrapping span takes the hover.
   */
  tooltip?: string;
}

interface ActionMenuProps {
  anchorEl: HTMLElement | null;
  /** Fallback position if anchorEl becomes stale */
  anchorPosition?: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  sections?: ActionMenuSection[];
  items?: ActionMenuItem[];
  title?: string;
}

interface SubMenuState {
  element: HTMLElement | null;
  position: { top: number; left: number };
}

// Delay before closing submenu (ms) - balance between responsiveness and usability
const SUBMENU_CLOSE_DELAY = 150;

const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  anchorPosition,
  open,
  onClose,
  sections,
  items,
  title,
}) => {
  const theme = useTheme();
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, SubMenuState>>({});
  const subMenuTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  // Helper: Check if keyA is an ancestor of keyB
  const isAncestorOf = useCallback((ancestor: string, descendant: string): boolean => {
    return descendant.startsWith(ancestor + '-item-');
  }, []);

  // Helper: Get parent key from a key
  const getParentKey = useCallback((key: string): string | null => {
    const lastItemIndex = key.lastIndexOf('-item-');
    if (lastItemIndex === -1) return null;
    return key.substring(0, lastItemIndex);
  }, []);

  // Reset submenu state when menu closes - this is a valid synchronization pattern
  // where we need to reset internal state in response to a prop change
  useEffect(() => {
    if (!open) {
      Object.values(subMenuTimeouts.current).forEach((timeout) => clearTimeout(timeout));
      subMenuTimeouts.current = {};

      setOpenSubMenus({});
    }
  }, [open]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(subMenuTimeouts.current).forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const cancelTimeout = useCallback((key: string) => {
    if (subMenuTimeouts.current[key]) {
      clearTimeout(subMenuTimeouts.current[key]);
      delete subMenuTimeouts.current[key];
    }
  }, []);

  const handleSubMenuOpen = useCallback(
    (key: string, element: HTMLElement) => {
      // Cancel ALL pending timeouts to prevent race conditions when moving quickly
      Object.keys(subMenuTimeouts.current).forEach((k) => {
        clearTimeout(subMenuTimeouts.current[k]);
        delete subMenuTimeouts.current[k];
      });

      // Capture position at the moment of open
      const rect = element.getBoundingClientRect();
      const position = { top: rect.top, left: rect.left };

      // Update open submenus: keep ancestors, add this one, close others
      setOpenSubMenus((prev) => {
        const next: Record<string, SubMenuState> = {};

        // Keep all ancestors of the new key
        Object.entries(prev).forEach(([existingKey, state]) => {
          if (isAncestorOf(existingKey, key)) {
            // existingKey is an ancestor of key - keep it
            next[existingKey] = state;
          }
          // Everything else (siblings, cousins, descendants) gets closed
        });

        // Add the new submenu with captured position
        next[key] = { element, position };

        return next;
      });
    },
    [isAncestorOf],
  );

  const handleSubMenuClose = useCallback(
    (key: string) => {
      cancelTimeout(key);

      // Set timeout to close - gives user time to move to submenu
      subMenuTimeouts.current[key] = setTimeout(() => {
        setOpenSubMenus((prev) => {
          const next: Record<string, SubMenuState> = {};

          // Keep everything except this key and its descendants
          Object.entries(prev).forEach(([existingKey, state]) => {
            if (existingKey !== key && !isAncestorOf(key, existingKey)) {
              next[existingKey] = state;
            }
          });

          return next;
        });
        delete subMenuTimeouts.current[key];
      }, SUBMENU_CLOSE_DELAY);
    },
    [cancelTimeout, isAncestorOf],
  );

  const handleNonSubMenuItemHover = useCallback(
    (key: string) => {
      // When hovering over an item without subItems, close any submenus
      // that are at the same level or deeper (siblings and their children)
      // but keep ancestors open

      const parentKey = getParentKey(key);

      setOpenSubMenus((prev) => {
        const next: Record<string, SubMenuState> = {};

        Object.entries(prev).forEach(([existingKey, state]) => {
          // Keep if existingKey is an ancestor of this item's parent (or is the parent)
          // i.e., keep submenus that contain this item
          if (
            parentKey &&
            (existingKey === parentKey || isAncestorOf(existingKey, parentKey + '-item-0'))
          ) {
            next[existingKey] = state;
          } else if (!parentKey) {
            // We're at the root level - close all submenus
            // (don't keep anything)
          } else if (isAncestorOf(existingKey, key)) {
            // existingKey is an ancestor of this item - keep it
            next[existingKey] = state;
          }
        });

        return next;
      });

      // Cancel all timeouts since we're making an immediate state change
      Object.keys(subMenuTimeouts.current).forEach((k) => {
        clearTimeout(subMenuTimeouts.current[k]);
        delete subMenuTimeouts.current[k];
      });
    },
    [getParentKey, isAncestorOf],
  );

  const handleItemClick = useCallback(
    (item: ActionMenuItem) => {
      if (item.onClick && !item.disabled && !item.subItems) {
        item.onClick();
        onClose();
      }
    },
    [onClose],
  );

  const renderMenuItem = (item: ActionMenuItem, index: number, parentKey = '') => {
    const key = `${parentKey}-item-${index}`;
    const hasSubItems = item.subItems && item.subItems.length > 0;

    if (item.divider) {
      return <Divider key={key} sx={{ my: 1 }} />;
    }

    const subMenuState = openSubMenus[key];
    const subMenuPosition = subMenuState?.position;
    const isSubMenuOpen = Boolean(subMenuState);

    const menuItem = (
      <MenuItem
        onClick={() => {
          if (!hasSubItems) {
            handleItemClick(item);
          }
        }}
        disabled={item.disabled}
        onMouseOver={(e) => {
          // Use onMouseOver instead of onMouseEnter - it fires even if element renders under cursor
          e.stopPropagation(); // Prevent bubbling to parent menu items
          if (hasSubItems) {
            handleSubMenuOpen(key, e.currentTarget);
          } else {
            handleNonSubMenuItemHover(key);
          }
        }}
        onMouseLeave={() => {
          if (hasSubItems) {
            handleSubMenuClose(key);
          }
        }}
        sx={{
          minHeight: 40,
          px: 2,
          color: item.danger ? theme.palette.error.main : theme.palette.text.primary,
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="body2">{item.label}</Typography>
          {hasSubItems && (
            <ChevronLeft sx={{ ml: 2, fontSize: 20, color: theme.palette.text.secondary }} />
          )}
        </Box>
      </MenuItem>
    );

    return (
      <React.Fragment key={key}>
        {item.tooltip ? (
          <Tooltip
            title={item.tooltip}
            // Describe rather than label the item, so it keeps its own accessible name.
            describeChild
            placement="right"
            slotProps={{
              popper: { modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] },
            }}
          >
            {/* A disabled MenuItem has no pointer events, so the span takes the hover;
                an enabled one stops mouseover propagation, so it must be the direct child. */}
            {item.disabled ? <span>{menuItem}</span> : menuItem}
          </Tooltip>
        ) : (
          menuItem
        )}

        {hasSubItems && isSubMenuOpen && (
          <Menu
            anchorReference="anchorPosition"
            anchorPosition={subMenuPosition}
            open={isSubMenuOpen}
            onClose={() => {
              // Clicking outside any submenu should close the entire menu
              onClose();
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            // MUI 9: both props needed to keep focus on the hovered parent (see root Menu)
            autoFocus={false}
            disableAutoFocus
            disableEnforceFocus
            disablePortal
            hideBackdrop
            sx={{
              pointerEvents: 'none', // Let clicks pass through the menu container
              '& .MuiPaper-root': {
                pointerEvents: 'auto', // But capture events on the actual menu paper
                backgroundColor: theme.palette.background.paper,
                backgroundImage: 'none',
                boxShadow: theme.shadows[8],
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 200,
              },
              '& .MuiMenuItem-root:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.04),
              },
            }}
            onMouseEnter={() => {
              // Cancel close timeout when mouse enters submenu
              cancelTimeout(key);
            }}
            onMouseLeave={() => {
              handleSubMenuClose(key);
            }}
          >
            {item.subItems?.map((subItem, subIndex) => renderMenuItem(subItem, subIndex, key))}
          </Menu>
        )}
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (sections) {
      return sections.map((section, sectionIndex) => (
        <React.Fragment key={`section-${sectionIndex}`}>
          {section.title && (
            <ListSubheader
              sx={{
                backgroundColor: 'transparent',
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                lineHeight: '28px',
                px: 2,
                py: 0.5,
                opacity: 0.7,
              }}
            >
              {section.title}
            </ListSubheader>
          )}
          {section.items.map((item, itemIndex) =>
            renderMenuItem(item, itemIndex, `section-${sectionIndex}`),
          )}
          {sectionIndex < sections.length - 1 && <Divider sx={{ my: 1 }} />}
        </React.Fragment>
      ));
    }

    if (items) {
      return items.map((item, index) => renderMenuItem(item, index));
    }

    return null;
  };

  // Determine anchoring strategy
  const hasPosition = Boolean(anchorPosition);
  const hasElement = Boolean(anchorEl);

  // Don't render if we have nothing to anchor to
  if (!open || (!hasPosition && !hasElement)) {
    return null;
  }

  return (
    <Menu
      anchorEl={hasPosition ? undefined : anchorEl}
      anchorReference={hasPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={anchorPosition ?? undefined}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      // MUI 9 Menu focuses its first item on open and its Popover takes focus
      // unless disableAutoFocus is passed too; keep focus on the trigger
      autoFocus={false}
      disableAutoFocus
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: theme.shadows[8],
          border: `1px solid ${theme.palette.divider}`,
          minWidth: 240,
          maxWidth: 320,
          mt: 1,
        },
        '& .MuiList-root': {
          py: 1,
        },
      }}
    >
      {title && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.4)
                : alpha(theme.palette.grey[100], 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              letterSpacing: '0.02em',
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
      {renderContent()}
    </Menu>
  );
};

export default ActionMenu;
